import { __assign } from "tslib";
import { getCurrentSite } from './cookie';
import { BuildMode, Datacenter, INTAKE_SITE } from './init';
import { getPathName, haveSameOrigin } from './urlPolyfill';
import { includes, ONE_KILO_BYTE, ONE_SECOND } from './utils';
export var DEFAULT_CONFIGURATION = {
    allowedTracingOrigins: [],
    isCollectingError: true,
    maxErrorsByMinute: 3000,
    maxInternalMonitoringMessagesPerPage: 15,
    resourceSampleRate: 100,
    sampleRate: 100,
    silentMultipleInit: false,
    trackInteractions: false,
    /**
     * arbitrary value, byte precision not needed
     */
    requestErrorResponseLengthLimit: 32 * ONE_KILO_BYTE,
    /**
     * flush automatically, aim to be lower than ALB connection timeout
     * to maximize connection reuse.
     */
    flushTimeout: 30 * ONE_SECOND,
    /**
     * Logs intake limit
     */
    maxBatchSize: 50,
    maxMessageSize: 256 * ONE_KILO_BYTE,
    /**
     * beacon payload max queue size implementation is 64kb
     * ensure that we leave room for logs, rum and potential other users
     */
    batchBytesLimit: 16 * ONE_KILO_BYTE,
};
export function buildConfiguration(userConfiguration, buildEnv) {
    var transportConfiguration = {
        applicationId: userConfiguration.applicationId,
        buildMode: buildEnv.buildMode,
        clientToken: userConfiguration.clientToken,
        env: userConfiguration.env,
        proxyHost: userConfiguration.proxyHost,
        sdkVersion: buildEnv.sdkVersion,
        service: userConfiguration.service,
        site: userConfiguration.site || INTAKE_SITE[userConfiguration.datacenter || buildEnv.datacenter],
        version: userConfiguration.version,
    };
    var enableExperimentalFeatures = Array.isArray(userConfiguration.enableExperimentalFeatures)
        ? userConfiguration.enableExperimentalFeatures
        : [];
    var configuration = __assign({ cookieOptions: {}, isEnabled: function (feature) {
            return includes(enableExperimentalFeatures, feature);
        }, logsEndpoint: getEndpoint('browser', transportConfiguration), proxyHost: userConfiguration.proxyHost, rumEndpoint: getEndpoint('rum', transportConfiguration), service: userConfiguration.service, traceEndpoint: getEndpoint('public-trace', transportConfiguration) }, DEFAULT_CONFIGURATION);
    if (userConfiguration.internalMonitoringApiKey) {
        configuration.internalMonitoringEndpoint = getEndpoint('browser', transportConfiguration, 'browser-agent-internal-monitoring');
    }
    if ('allowedTracingOrigins' in userConfiguration) {
        configuration.allowedTracingOrigins = userConfiguration.allowedTracingOrigins;
    }
    if ('isCollectingError' in userConfiguration) {
        configuration.isCollectingError = !!userConfiguration.isCollectingError;
    }
    if ('sampleRate' in userConfiguration) {
        configuration.sampleRate = userConfiguration.sampleRate;
    }
    if ('resourceSampleRate' in userConfiguration) {
        configuration.resourceSampleRate = userConfiguration.resourceSampleRate;
    }
    if ('trackInteractions' in userConfiguration) {
        configuration.trackInteractions = !!userConfiguration.trackInteractions;
    }
    configuration.cookieOptions.secure = mustUseSecureCookie(userConfiguration);
    configuration.cookieOptions.crossSite = !!userConfiguration.useCrossSiteSessionCookie;
    if (!!userConfiguration.trackSessionAcrossSubdomains) {
        configuration.cookieOptions.domain = getCurrentSite();
    }
    if (transportConfiguration.buildMode === BuildMode.E2E_TEST) {
        if (userConfiguration.internalMonitoringEndpoint !== undefined) {
            configuration.internalMonitoringEndpoint = userConfiguration.internalMonitoringEndpoint;
        }
        if (userConfiguration.logsEndpoint !== undefined) {
            configuration.logsEndpoint = userConfiguration.logsEndpoint;
        }
        if (userConfiguration.rumEndpoint !== undefined) {
            configuration.rumEndpoint = userConfiguration.rumEndpoint;
        }
    }
    if (transportConfiguration.buildMode === BuildMode.STAGING) {
        if (userConfiguration.replica !== undefined) {
            var replicaTransportConfiguration = __assign(__assign({}, transportConfiguration), { applicationId: userConfiguration.replica.applicationId, clientToken: userConfiguration.replica.clientToken, site: INTAKE_SITE[Datacenter.US] });
            configuration.replica = {
                applicationId: userConfiguration.replica.applicationId,
                internalMonitoringEndpoint: getEndpoint('browser', replicaTransportConfiguration, 'browser-agent-internal-monitoring'),
                logsEndpoint: getEndpoint('browser', replicaTransportConfiguration),
                rumEndpoint: getEndpoint('rum', replicaTransportConfiguration),
            };
        }
    }
    return configuration;
}
function getEndpoint(type, conf, source) {
    var tags = "sdk_version:" + conf.sdkVersion +
        ("" + (conf.env ? ",env:" + conf.env : '')) +
        ("" + (conf.service ? ",service:" + conf.service : '')) +
        ("" + (conf.version ? ",version:" + conf.version : ''));
    var datadogHost = type + "-http-intake.logs." + conf.site;
    var host = conf.proxyHost ? conf.proxyHost : datadogHost;
    var proxyParameter = conf.proxyHost ? "ddhost=" + datadogHost + "&" : '';
    var applicationIdParameter = conf.applicationId ? "_dd.application_id=" + conf.applicationId + "&" : '';
    var parameters = "" + applicationIdParameter + proxyParameter + "ddsource=" + (source || 'browser') + "&ddtags=" + tags;
    return "https://" + host + "/v1/input/" + conf.clientToken + "?" + parameters;
}
export function isIntakeRequest(url, configuration) {
    return (getPathName(url).indexOf('/v1/input/') === 0 &&
        (haveSameOrigin(url, configuration.logsEndpoint) ||
            haveSameOrigin(url, configuration.rumEndpoint) ||
            haveSameOrigin(url, configuration.traceEndpoint) ||
            (!!configuration.internalMonitoringEndpoint && haveSameOrigin(url, configuration.internalMonitoringEndpoint)) ||
            (!!configuration.replica &&
                (haveSameOrigin(url, configuration.replica.logsEndpoint) ||
                    haveSameOrigin(url, configuration.replica.rumEndpoint) ||
                    haveSameOrigin(url, configuration.replica.internalMonitoringEndpoint)))));
}
export function mustUseSecureCookie(userConfiguration) {
    return !!userConfiguration.useSecureSessionCookie || !!userConfiguration.useCrossSiteSessionCookie;
}
//# sourceMappingURL=configuration.js.map